export type TestimonialItem = {
  id: string;
  content: string;
  author: string | null;
  order: number;
  /** 1–5 stars; only present on engagement-verified client testimonials */
  rating?: number | null;
  /** "client" = engagement-verified; "coach" = manually added */
  source?: string;
};

export interface TestimonialAggregate {
  average: number;
  count: number;
}

/** Average star rating across engagement-verified client testimonials. */
export function getTestimonialAggregate(
  testimonials: readonly TestimonialItem[],
): TestimonialAggregate | null {
  const rated = testimonials.filter(
    (t) => t.source === "client" && t.rating != null,
  );
  if (rated.length === 0) return null;
  const average =
    rated.reduce((sum, t) => sum + (t.rating ?? 0), 0) / rated.length;
  return { average, count: rated.length };
}

export function getFeaturedTestimonial<T extends TestimonialItem>(
  testimonials: readonly T[],
): T | null {
  if (testimonials.length === 0) return null;
  let best = testimonials[0];
  for (let i = 1; i < testimonials.length; i++) {
    if (testimonials[i].order < best.order) best = testimonials[i];
  }
  return best;
}

export function getRemainingTestimonials<T extends TestimonialItem>(
  testimonials: readonly T[],
): T[] {
  if (testimonials.length <= 1) return [];
  const featured = getFeaturedTestimonial(testimonials);
  return testimonials
    .filter((t) => t !== featured)
    .sort((a, b) => a.order - b.order);
}
